import { createContext, useCallback, useContext, useMemo, useState, type JSX } from 'react';
import {
  DEFAULT_LANG,
  SUPPORTED_LANGS,
  isSupportedLang,
  type LangCode,
} from '@/lib/supported-langs';

const STORAGE_KEY = 'wandero.admin.lang';

interface LangContextValue {
  lang: LangCode;
  setLang: (code: LangCode) => void;
  languages: LangCode[];
}

const LangContext = createContext<LangContextValue | null>(null);

function readStoredLang(): LangCode {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && isSupportedLang(raw)) return raw;
  } catch {
    /* ignore */
  }
  return DEFAULT_LANG;
}

export function LangProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [lang, setLangState] = useState<LangCode>(readStoredLang);

  const setLang = useCallback((code: LangCode): void => {
    setLangState(code);
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<LangContextValue>(
    () => ({ lang, setLang, languages: SUPPORTED_LANGS }),
    [lang, setLang],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used within a LangProvider');
  return ctx;
}
